import React from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Security as SecurityIcon,
  Info as InfoIcon,
  Policy as PolicyIcon,
  CheckCircle as CheckCircleIcon,
  PhoneIphone as PhoneIcon,
  AppShortcut as AppIcon
} from '@mui/icons-material';
import { t } from '../utils/translations';

const PrivacyPolicy = () => {
  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 6 }}>
      <Paper elevation={2} sx={{ p: 3, borderRadius: 2, mb: 4 }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main', display: 'flex', alignItems: 'center', gap: 1 }}>
          <PolicyIcon fontSize="large" /> {t('Privacy Policy')}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
          <AppIcon fontSize="small" color="secondary" />
          <Typography variant="h6" color="text.secondary">
            Bambino {t('Workshop')} iOS App
          </Typography>
        </Box>
        <Typography variant="subtitle1" color="text.secondary" sx={{ mt: 1 }}>
          {t('Last updated')}: 16 {t('April')} 2025
        </Typography>
      </Paper>

      <Paper elevation={1} sx={{ p: 4, borderRadius: 2 }}>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
            <InfoIcon /> {t('Introduction')}
          </Typography>
          <Typography paragraph>
            {t('This Privacy Policy describes how we collect, use, process, and disclose your information, including personal information, in conjunction with your access to and use of the Bambino Workshop iOS application.')}
          </Typography>
          <Typography paragraph>
            {t('When this policy mentions "we," "us," or "our," it refers to Bambino Workshop that is responsible for your information under this Privacy Policy.')}
          </Typography>
        </Box>

        <Divider sx={{ my: 4 }} />
        
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
            <PhoneIcon /> {t('App Information')}
          </Typography>
          <Box sx={{ 
            p: 2, 
            borderRadius: 2, 
            bgcolor: 'rgba(236, 36, 143, 0.05)', 
            border: '1px dashed', 
            borderColor: 'rgba(236, 36, 143, 0.3)',
            mb: 2
          }}>
            <Typography variant="body1" paragraph sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <strong>{t('App Name')}:</strong> Bambino Цех
            </Typography>
            <Typography variant="body1" paragraph sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <strong>{t('Platform')}:</strong> iOS
            </Typography>
            <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <strong>{t('App Store Link')}:</strong> [App Store]
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ my: 4 }} />

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
            <SecurityIcon /> {t('Information We Collect')}
          </Typography>
          <Typography paragraph>
            {t('We collect various types of information through our iOS app to provide and improve our services to you:')}
          </Typography>

          <List sx={{ mt: 2 }}>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon sx={{ color: 'primary.main' }} />
              </ListItemIcon>
              <ListItemText 
                primary={t('Account Information')} 
                secondary={t('We collect information such as your name, phone number, email address, and other details you provide when registering with the Bambino Цех app.')}
                primaryTypographyProps={{ fontWeight: 'bold' }}
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon sx={{ color: 'primary.main' }} />
              </ListItemIcon>
              <ListItemText 
                primary={t('Transaction Information')} 
                secondary={t('We collect information related to your requests, including products, quantities, and order history submitted through the app.')}
                primaryTypographyProps={{ fontWeight: 'bold' }}
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon sx={{ color: 'primary.main' }} />
              </ListItemIcon>
              <ListItemText 
                primary={t('Device Information')} 
                secondary={t('We may collect information about your iOS device, including device model, operating system version, unique device identifiers, and mobile network information.')}
                primaryTypographyProps={{ fontWeight: 'bold' }}
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon sx={{ color: 'primary.main' }} />
              </ListItemIcon>
              <ListItemText 
                primary={t('Usage Information')} 
                secondary={t('We collect information about how you interact with our app, including features you use, time spent on the app, and other activities.')}
                primaryTypographyProps={{ fontWeight: 'bold' }}
              />
            </ListItem>
          </List>
        </Box>

        <Divider sx={{ my: 4 }} />

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
            <SecurityIcon /> {t('How We Use Your Information')}
          </Typography>
          <Typography paragraph>
            {t('We use the information we collect for various purposes, including:')}
          </Typography>

          <List sx={{ mt: 2 }}>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon sx={{ color: 'primary.main' }} />
              </ListItemIcon>
              <ListItemText 
                primary={t('Providing Services')} 
                secondary={t('To provide, maintain, and improve our app features and functionality for you.')}
                primaryTypographyProps={{ fontWeight: 'bold' }}
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon sx={{ color: 'primary.main' }} />
              </ListItemIcon>
              <ListItemText 
                primary={t('Communication')} 
                secondary={t('To communicate with you about your requests, app updates, and other customer support matters.')}
                primaryTypographyProps={{ fontWeight: 'bold' }}
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon sx={{ color: 'primary.main' }} />
              </ListItemIcon>
              <ListItemText 
                primary={t('Security')} 
                secondary={t('To detect, prevent, and address security issues and unauthorized access to the app.')}
                primaryTypographyProps={{ fontWeight: 'bold' }}
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon sx={{ color: 'primary.main' }} />
              </ListItemIcon>
              <ListItemText 
                primary={t('App Performance')} 
                secondary={t('To analyze app performance, fix bugs, and optimize the user experience.')}
                primaryTypographyProps={{ fontWeight: 'bold' }}
              />
            </ListItem>
          </List>
        </Box>

        <Divider sx={{ my: 4 }} />

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
            <SecurityIcon /> {t('Data Retention')}
          </Typography>
          <Typography paragraph>
            {t('We retain your personal information for as long as necessary to provide you with our app services and as required by applicable law. We may retain your information for a longer period if required for legal obligations or to resolve disputes.')}
          </Typography>
        </Box>

        <Divider sx={{ my: 4 }} />

        <Box>
          <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
            <InfoIcon /> {t('Contact Us')}
          </Typography>
          <Typography paragraph>
            {t('If you have any questions about this Privacy Policy or our data practices, please contact us at:')}
          </Typography>
          <Typography variant="body1" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
            yerzhanserikbay@brotech.kz
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
};

export default PrivacyPolicy; 